import { inject, injectable } from "inversify";
import { ScenarioElement } from "../../../model/playerConfiguration/ScenarioElement";
import { ScenarioGlobalState } from "../../../model/scenarioGlobalState/ScenarioGlobalState";
import { TYPES } from "../../di/types";
import { PosterLayer } from "../../layer/PosterLayer";
import { VideoLayer } from "../../layer/VideoLayer";
import { ResourcesCache } from "../../loader/scenarioLoader/ResourcesCache";

export interface PosterLayerManager {
  newScenarioElement(scenarioGlobalState: ScenarioGlobalState): void;
  resourcesLoaded(resourcesCache: ResourcesCache): void;
  scenarioElementEnded(): void;
  setCurrentVideoSnapshotAsPoster(): void;
  hidePoster(): void;
  showInitialPoster(posterUrl: string, aspectRatio: number): void;
  showCachedPoster(posterUrl: string): void;
  getIsShowingPoster(): boolean;
}

@injectable()
export class PosterLayerManagerImpl implements PosterLayerManager {

  private scenarioElement: ScenarioElement;
  private resourcesCache: ResourcesCache;

  constructor(
    @inject(TYPES.layer.PosterLayer) private posterLayer: PosterLayer,
    @inject(TYPES.layer.VideoLayer) private videoLayer: VideoLayer) {}

  newScenarioElement(scenarioGlobalState: ScenarioGlobalState): void {
    this.scenarioElement = scenarioGlobalState.scenarioPath.currentElement;
  }

  resourcesLoaded(resourcesCache: ResourcesCache): void {
    this.resourcesCache = resourcesCache;
    if (this.scenarioElement.isCTAOnlyElement && this.scenarioElement.poster) {
      this.showCachedPoster(this.scenarioElement.poster);
    }
  }

  scenarioElementEnded(): void {
    if (this.scenarioElement.video) {
      const triggerTypeEndCTA = this.scenarioElement.ctas.find((cta) => cta.triggerType === "END");
      if (!triggerTypeEndCTA || !triggerTypeEndCTA.poster) {
        this.setCurrentVideoSnapshotAsPoster();
      }
    }
  }

  setCurrentVideoSnapshotAsPoster(): void {
    if (this.scenarioElement.video) {
      const videoSnapshot = this.videoLayer.captureSnapshot();
      if (videoSnapshot) {
        this.doShowPoster(videoSnapshot);
      }
    }
  }

  hidePoster(): void {
    this.posterLayer.hidePoster();
  }

  showInitialPoster(posterUrl: string, aspectRatio: number) {
    this.posterLayer.setAspectRatio(aspectRatio);
    // this poster isn't cached
    this.doShowPoster(posterUrl);
  }

  showCachedPoster(posterUrl: string) {
    const posterObjectUrl = this.resourcesCache.posterObjectUrlsByUrl.get(posterUrl);
    this.doShowPoster(posterObjectUrl);
  }

  getIsShowingPoster(): boolean {
    return this.posterLayer.getIsShowingPoster();
  }

  private doShowPoster(posterUrl: string): void {
    this.posterLayer.showPoster(posterUrl);
  }

}